import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';

interface ChartGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const ChartGuideModal: React.FC<ChartGuideModalProps> = ({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop — above header/filter sheets */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        style={{ zIndex: 'var(--z-overlay-chart-guide, 10050)' }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="fixed inset-0 overflow-y-auto overscroll-contain"
        style={{ zIndex: 'calc(var(--z-overlay-chart-guide, 10050) + 1)' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="chart-guide-modal-title"
      >
        <div className="flex min-h-full items-center justify-center p-4 sm:p-6 md:p-8 text-center">
          <div
            style={{ borderRadius: 'var(--card-border-radius)' }}
            className="relative w-full max-w-2xl mx-auto transform overflow-hidden bg-bg-card text-right shadow-xl transition-all my-4 sm:my-8 animate-fadeIn pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-bg-primary px-4 sm:px-6 md:px-8 py-4 sm:py-6 border-b border-border-primary">
              <div className="flex justify-end mb-2 sm:mb-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full min-w-[44px] min-h-[44px] w-11 h-11 hover:bg-red-500 hover:bg-opacity-20 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center justify-center"
                  aria-label="بستن"
                >
                  <X size={24} className="text-red-500 hover:text-red-600" />
                </button>
              </div>
              <div className="flex justify-center">
                <h2 id="chart-guide-modal-title" className="title-section text-base sm:text-lg md:text-xl px-2">
                  {title}
                </h2>
              </div>
            </div>

            {/* Body */}
            <div
              className="bg-bg-card px-4 sm:px-8 md:px-12 py-6 sm:py-8 max-h-[min(70vh,32rem)] overflow-y-auto"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {children}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ChartGuideModal;
